import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

import { Icon } from '@/atoms';
import { Card } from '@/molecules';

import BeCasaLogo from '@/assets/domainBrand/logo-prices-primary.png';
import CheckMarkCircle from '@/assets/icons/CheckmarkCircle.svg';
import NoDisponible from '@/assets/icons/NoDisponible.svg';

import type { FunctionComponent } from 'react';
import type { TypePriceDetailCardProps } from './types';

/**
 * PriceDetailCard
 */
export const PriceDetailCard: FunctionComponent<TypePriceDetailCardProps> = ({
  className = '',
  beCasaPrice,
  traditionalPrice,
  detalles,
  disclaimer = '',
}: TypePriceDetailCardProps) => {
  const { t } = useTranslation('common');
  return (
    <Card className={`flex w-fit ${className}`}>
      <table className="w-full text-primary-400">
        <thead className="border-b">
          <tr className="flex justify-between">
            <th className="w-3/6" />
            <th className="w-1/& flex justify-center">
              <Image
                src={BeCasaLogo}
                height={36}
                width={81}
                alt="Logo Be Casa"
              />
            </th>
            <th className="flex w-1/3 justify-end">
              <span className="text-body price_detail-traditional text-body--secondary">
                {t('components.organisms.priceDetail.traditionalRenting')}
              </span>
            </th>
            <th />
          </tr>
        </thead>
        <tbody className="flex flex-col">
          {detalles.map((element, index) => (
            <tr
              key={index}
              className={`mt-3 flex flex-row items-center gap-2 ${
                index + 1 !== detalles.length ? 'border-b' : ''
              } border-primary-200`}
            >
              <td className="flex w-3/6 flex-row items-center  gap-2">
                <Icon src={element.icon} height={15} width={15} />
                <span className="text-body text-body--secondary flex justify-center">
                  {element.text}
                </span>
              </td>
              <td className="flex w-1/6 items-center justify-center">
                {element.isInBeCasa ? (
                  <CheckMarkCircle width={14} heigth={14} />
                ) : (
                  <NoDisponible width={14} heigth={14} />
                )}
              </td>
              <td className="text-body text-body--secondary flex w-1/3 items-center justify-end pr-2 sm:pr-6">
                {element.traditionalPrice ? (
                  <span>{element.traditionalPrice} €</span>
                ) : (
                  <NoDisponible className="mr-[13px]" width={14} heigth={14} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="mt-1 flex justify-between">
          <tr className="text-body price_detail-price text-body--secondary--bold">
            <td className="ml-2 w-3/6">
              {t('components.organisms.priceDetail.totalCost')}
            </td>
            <td className="priceDetailFooter flex w-1/6 justify-center">
              <span className="w-fit">{beCasaPrice}€</span>
              <span className="w-fit">
                {t('components.organisms.priceDetail.perMonth')}*
              </span>
            </td>
            <td className="priceDetailFooter flex w-1/3">
              <span className="w-fit">{traditionalPrice}€</span>
              <span className="w-fit">
                {t('components.organisms.priceDetail.perMonth')}*
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
      <p className="text-other text-other--detail mt-6 text-primary-300">
        {disclaimer}
      </p>

      <div />
    </Card>
  );
};

PriceDetailCard.displayName = 'PriceDetailCard';
